import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/session-status");
      setUser(data.user || null);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth callback, skip the /me check and let AuthCallback handle it
    if (typeof window !== "undefined" && window.location.hash && window.location.hash.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Global 401 handler from the axios interceptor: session expired/invalid → clear user
  useEffect(() => {
    const onUnauthorized = () => setUser((prev) => (prev ? null : prev));
    window.addEventListener("vyrti:unauthorized", onUnauthorized);
    return () => window.removeEventListener("vyrti:unauthorized", onUnauthorized);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    track("login", { method: "email" });
    setUser(data.user);
    return data.user;
  };

  const register = async (firstName, lastName, email, password) => {
    const { data } = await api.post("/auth/register", {
      first_name: firstName,
      last_name: lastName,
      email,
      password,
    });
    track("sign_up", { method: "email" });
    setUser(data.user);
    return data.user;
  };

  const exchangeSession = async (sessionId) => {
    const { data } = await api.post("/auth/session", { session_id: sessionId });
    track("login", { method: "google" });
    setUser(data.user);
    return data.user;
  };

  const loginWithApple = async (idToken) => {
    const { data } = await api.post("/auth/apple", { id_token: idToken });
    track("login", { method: "apple" });
    setUser(data.user);
    return data.user;
  };

  const resendVerification = async () => {
    const { data } = await api.post("/auth/resend-verification");
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    setUser(null);
  };

  const refreshUser = checkAuth;

  return (
    <AuthContext.Provider value={{ user, loading, login, register, exchangeSession, loginWithApple, logout, refreshUser, resendVerification }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
